/** Default message when limit error response cannot be parsed (upload/storage/transcript limits). */
const DEFAULT_LIMIT_ERROR_MESSAGE = "Upload limit exceeded.";

/**
 * Utility function to handle limit error responses (403 status) from the API.
 * Extracts error message from JSON response body or returns default message.
 *
 * @param response - The fetch Response object
 * @param defaultMessage - Default error message if response parsing fails (defaults to generic upload limit message)
 * @returns Promise<string> - The error message to display
 */
export async function handleLimitError(
  response: Response,
  defaultMessage: string = DEFAULT_LIMIT_ERROR_MESSAGE,
): Promise<string> {
  if (response.status === 403) {
    try {
      const body = await response.json();
      return body.error || defaultMessage;
    } catch {
      return defaultMessage;
    }
  }
  return defaultMessage;
}

/** Text of the link under a limit message (the message box adds the "→"). */
export const SUBSCRIBE_LINK_TEXT = "Subscribe";

/** '1 h 20 min' · '1 h' · '40 min' · 'under a minute' — same wording as the backend message. */
export function formatDurationShort(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  if (s < 60) return "under a minute";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h && m) return `${h} h ${m} min`;
  if (h) return `${h} h`;
  return `${m} min`;
}

/**
 * A refused upload (403 from the upload gate): the server's message plus a link to the plans
 * page that carries the video's length and the time left, so that page can say why you're
 * there. A silent jump to a generic pricing page read like an ad, not an answer (2026-10-08).
 */
export async function readLimitError(
  response: Response,
): Promise<{ message: string; plansUrl: string; link: LimitLink }> {
  let body: Record<string, unknown> = {};
  try {
    body = await response.json();
  } catch {
    /* not JSON — fall back to the default message */
  }
  return {
    message: typeof body.error === "string" && body.error ? body.error : DEFAULT_LIMIT_ERROR_MESSAGE,
    plansUrl: limitPlansUrl(body),
    link: limitLink(body),
  };
}

export interface LimitLink {
  text: string;
  url: string;
}

/**
 * The way out under a limit message, by what the server says comes next (`next_step`):
 * a free account subscribes, Creator upgrades, and Pro — the top plan — contacts us, since
 * "Upgrade" would only lead back to the plan it already has.
 */
export function limitLink(body: Record<string, unknown>): LimitLink {
  if (body?.next_step === "contact") return { text: "Contact us", url: "/contact/" };
  return {
    text: body?.next_step === "upgrade" ? "Upgrade" : SUBSCRIBE_LINK_TEXT,
    url: limitPlansUrl(body),
  };
}

/** The plans page for an already-parsed 403 body (see readLimitError). */
export function limitPlansUrl(body: Record<string, unknown>): string {
  const params = new URLSearchParams();
  const video = Number(body?.video_seconds);
  const left = Number(body?.seconds_left);
  if (body?.limit_type === "transcript" && Number.isFinite(video) && video > 0) {
    params.set("video", String(Math.round(video)));
    if (Number.isFinite(left)) params.set("left", String(Math.round(left)));
  }
  const qs = params.toString();
  return `/subscription/${qs ? `?${qs}` : ""}`;
}
